import { Camera, Clock, Cross, ShieldCheck, Sparkles, Sun, Zap, Car } from "lucide-react";
import Reveal from "./Reveal";
import { PROCESS } from "@/config/clinicConfig";

const ICONS = { camera: Camera, shield: ShieldCheck, zap: Zap, sparkles: Sparkles };

export default function Process() {
  return (
    <section id="process" data-testid="process-section" className="bg-sand pt-10 pb-24 lg:pt-14 lg:pb-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <div className="flex items-start gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-clay/10 text-clay">
              <Cross size={26} />
            </span>
            <h2 className="max-w-2xl text-balance font-display text-3xl font-medium leading-snug tracking-tight text-espresso sm:text-4xl lg:text-[2.75rem]">
              {PROCESS.heading}
            </h2>
          </div>
        </Reveal>

        <div className="mt-10 grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
          {PROCESS.steps.map((step, i) => {
            const Icon = ICONS[step.icon] || Sparkles;
            return (
              <Reveal key={step.n} delay={i * 0.08}>
                <div
                  data-testid={`process-step-${i + 1}`}
                  className="group h-full rounded-3xl border border-espresso/10 bg-cream/80 p-7 transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_50px_-24px_rgba(44,34,30,0.35)]"
                >
                  <div className="flex items-center justify-between">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-clay/10 text-clay transition-colors duration-300 group-hover:bg-clay group-hover:text-cream">
                      <Icon size={19} />
                    </span>
                    <span className="font-mono text-sm text-gold">{step.n}</span>
                  </div>
                  <h3 className="mt-6 font-display text-xl font-semibold tracking-tight text-espresso">
                    {step.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-cocoa">{step.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.2}>
          <div
            data-testid="recovery-banner"
            className="mt-6 overflow-hidden rounded-3xl border-2 border-gold/60 bg-cream p-8 shadow-[0_24px_60px_-30px_rgba(44,34,30,0.4)] sm:p-10"
          >
            <div className="flex flex-col items-start gap-7 sm:flex-row sm:items-center">
              <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-clay text-cream sm:h-24 sm:w-24">
                <Clock size={42} />
              </div>
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-clay">
                  The Pinpoint difference
                </p>
                <p className="mt-2 font-display text-3xl font-semibold tracking-tight text-espresso sm:text-4xl">
                  Most clients are back to normal in <span className="italic text-clay">about thirty minutes.</span>
                </p>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-cocoa sm:text-base">
                  Our proprietary process delivers amazing recovery — the majority of clients leave looking
                  normal within thirty minutes. Some people experience a little swelling or bruising, but it
                  settles quickly.
                </p>
                <div className="mt-4 flex flex-wrap gap-x-7 gap-y-2" data-testid="recovery-tips">
                  <span className="flex items-center gap-2 text-[13px] font-semibold text-espresso">
                    <Car size={16} className="text-clay" />
                    You can drive yourself home
                  </span>
                  <span className="flex items-center gap-2 text-[13px] font-semibold text-espresso">
                    <Sun size={16} className="text-clay" />
                    Bring a hat &amp; sunglasses for the bright Phoenix sun — especially in the summer months
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.25}>
          <div className="mt-6 flex flex-col items-start gap-5 rounded-3xl bg-espresso p-8 text-cream sm:flex-row sm:items-center sm:p-10">
            <span className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-full bg-cream shadow-[0_10px_30px_-12px_rgba(0,0,0,0.6)] ring-1 ring-gold/40">
              <img
                src="/corneal-shield.png"
                alt="Medical-grade corneal eye shield used during every laser session"
                className="h-full w-full object-contain p-1.5"
              />
            </span>
            <div>
              <h3 className="font-display text-xl font-semibold text-cream sm:text-2xl">
                {PROCESS.callout.title}
              </h3>
              <p className="mt-2 max-w-3xl text-sm leading-relaxed text-cream/75 sm:text-base">
                {PROCESS.callout.body}
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
